import { useInfiniteQuery, useMutation, useQuery, useQueryClient, type UseMutationResult, type UseQueryResult } from '@tanstack/react-query';
import { api, requestForm } from '../lib/api';
import { qk } from './keys';
import type { SupportMessage, SupportThread } from '../types/api';

export function useSupportInbox(offset: number) {
  return useQuery({ queryKey: qk.supportInbox(offset), queryFn: () => api.get<SupportThread[]>('/support', { offset }) });
}
export function useSupportMessages(studentId: string) {
  return useInfiniteQuery({ queryKey: qk.supportMessages(studentId), enabled: Boolean(studentId),
    initialPageParam: undefined as string | undefined,
    queryFn: ({ pageParam }) => api.get<SupportMessage[]>(`/support/${studentId}/messages`, { before: pageParam }),
    getNextPageParam: (page) => page.length === 50 ? page[0]?.id : undefined });
}
export function useSendSupport(studentId: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: (body: string) => api.post<SupportMessage>(`/support/${studentId}/messages`, { body }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: qk.support }); } });
}
export function useReadSupport(studentId: string) {
  const client = useQueryClient();
  return useMutation({ mutationFn: (throughId: string) => api.post<{ marked: number }>(`/support/${studentId}/read`, { throughId }),
    onSuccess: () => { void client.invalidateQueries({ queryKey: qk.support }); } });
}

export function useSendSupportVoice(studentId: string): UseMutationResult<SupportMessage, Error, File> {
  const client = useQueryClient();
  return useMutation({ mutationFn: (file: File) => {
    const form = new FormData(); form.set('file', file);
    return requestForm<SupportMessage>(`/support/${studentId}/voice`, form);
  }, onSuccess: () => { void client.invalidateQueries({ queryKey: qk.support }); } });
}

export function useSupportUnread(enabled: boolean): UseQueryResult<{ unreadCount: number }, Error> {
  return useQuery({ queryKey: qk.supportUnread, enabled,
    queryFn: () => api.get<{ unreadCount: number }>('/support/unread') });
}

export function useSendSupportImage(studentId: string): UseMutationResult<SupportMessage, Error, File> {
  const client = useQueryClient();
  return useMutation({ mutationFn: (file: File) => {
    const form = new FormData(); form.set('file', file);
    return requestForm<SupportMessage>(`/support/${studentId}/image`, form);
  }, onSuccess: () => { void client.invalidateQueries({ queryKey: qk.support }); } });
}
